import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { socialPostPath } from '../../../social/socialPostPath';
import { kitAssetLibraryFile, libraryPostAspect, reelLibraryFile } from '../../library/libraryGroups';
import { usePostLibraryFile } from '../../library/usePostLibraryFile';
import { aiReelStudioParams } from '../../studioRoute';
import { PostsBoardFile, webAssetOf, webReelOf } from '../postsModel';

export interface PostFileActions {
  /** True when the Composer can take the file (the motion artwork and an odd shape cannot). */
  canPost: (file: PostsBoardFile) => boolean;
  /** "Post it": the Composer with the file and its release (design c1). */
  post: (file: PostsBoardFile) => void;
  /** "Review" of an AI reel in review: the reel page of the Studio, with Approve and Reject (decision 7). */
  review: (file: PostsBoardFile) => void;
  openCalendar: (postUuid?: string) => void;
  openSocial: (socialPostId: number) => void;
  /** "Plan them": the Plan tab of the song. */
  openPlan: (catalogItemUuid: string) => void;
  /** "See" of the phone Making line: the Posts tab of the release of the song. */
  openReleasePosts: (catalogItemUuid: string) => void;
  openStudio: () => void;
  newPost: () => void;
  openReleases: () => void;
  /** "Add my song": the artist has no song to plan. */
  addSong: () => void;
}

/** The actions of the Posts page. `songs` are the catalog rows of the artist. */
export function usePostFileActions(songs: ReadonlyArray<CatalogItem>): PostFileActions {
  const router = useSMLRouter();
  const navigate = useNavigate();
  const postFile = usePostLibraryFile();
  const songByUuid = useMemo(() => new Map(songs.map((song) => [song.uuid, song])), [songs]);

  const libraryFileOf = useCallback((file: PostsBoardFile) => {
    const reel = webReelOf(file);
    if (reel !== null) {
      return reelLibraryFile(reel);
    }
    const asset = webAssetOf(file);
    return asset === null ? null : kitAssetLibraryFile(asset);
  }, []);

  const canPost = useCallback(
    (file: PostsBoardFile): boolean => {
      const libraryFile = libraryFileOf(file);
      return libraryFile !== null && libraryPostAspect(libraryFile) !== null && file.catalogItemUuid !== null;
    },
    [libraryFileOf]
  );

  const post = useCallback(
    (file: PostsBoardFile) => {
      const song = file.catalogItemUuid === null ? undefined : songByUuid.get(file.catalogItemUuid);
      const libraryFile = libraryFileOf(file);
      if (song !== undefined && libraryFile !== null) {
        postFile(song, libraryFile);
      }
    },
    [libraryFileOf, postFile, songByUuid]
  );

  const review = useCallback(
    (file: PostsBoardFile) => {
      if (file.catalogItemUuid !== null) {
        router.push(
          SMLRoutes.promoteStudio,
          aiReelStudioParams({ catalogItemUuid: file.catalogItemUuid, reel: file.uuid })
        );
      }
    },
    [router]
  );

  const openCalendar = useCallback(
    (postUuid?: string) =>
      router.push(SMLRoutes.promoteCalendar, postUuid === undefined ? undefined : { post: postUuid }),
    [router]
  );
  const openSocial = useCallback(
    (socialPostId: number) => {
      Promise.resolve(navigate(socialPostPath(socialPostId))).catch(console.error);
    },
    [navigate]
  );
  const openPlan = useCallback(
    (catalogItemUuid: string) => router.push(SMLRoutes.promoteRelease, { catalogItemUuid, tab: 'plan' }),
    [router]
  );
  const openReleasePosts = useCallback(
    (catalogItemUuid: string) => router.push(SMLRoutes.promoteRelease, { catalogItemUuid, tab: 'posts' }),
    [router]
  );
  const openStudio = useCallback(() => router.push(SMLRoutes.promoteStudio), [router]);
  const newPost = useCallback(() => router.push(SMLRoutes.promoteComposer), [router]);
  const openReleases = useCallback(() => router.push(SMLRoutes.promote), [router]);
  const addSong = useCallback(() => router.push(SMLRoutes.addSong), [router]);

  return {
    canPost,
    post,
    review,
    openCalendar,
    openSocial,
    openPlan,
    openReleasePosts,
    openStudio,
    newPost,
    openReleases,
    addSong,
  };
}
